import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { styled } from '@mui/material/styles';
import { Loader } from '@tkww/the-bash-frontend';
import { gmConfig } from 'helpers/config';
import { HeroWithConfetti } from 'components/Hero';
import { SectionContainer } from 'components/grid';
import Select from 'components/Select';
import { ctaClickEvent } from 'state/modules/analytics';
import {
  RISING_STAR_CLICK_TARGET,
  RISING_STAR_CLICK_AREA,
  DROPDOWN_CTA_STYLE,
} from 'analytics/constants';
import RisingStarApi from 'api/risingStar';
import AwardGroupWinnerList from 'views/RisingStarPage/AwardGroupWinnerList';
import { awardGroupPropType } from 'views/RisingStarPage/propTypes';
import { RISING_STAR_PAGE_DESCRIPTION } from 'views/RisingStarPage/constants';
import { RISING_STAR_PAGE_NAME } from 'constants/pageNames';
import MetaDataMarkup from 'components/MetaDataMarkup';
import { CONFETTI_HERO_IMAGE_LEFT, CONFETTI_HERO_IMAGE_RIGHT } from 'constants/heros';

const StyledSelect = styled(Select)(({ theme }) => ({
  maxWidth: 343,
  margin: '32px auto 0',
  [theme.breakpoints.up('md')]: {
    maxWidth: 219,
    marginTop: 16,
  },
}));

const RisingStarPageView = ({
  title,
  awardGroups,
  year,
  publishedYears,
  metaTitle,
  metaDescription,
}) => {
  const dispatch = useDispatch();

  const yearOptions = publishedYears.map((risingStarYear) => ({
    label: `${risingStarYear} Winners`,
    value: risingStarYear,
  }));

  const [dropdownYear, setDropdownYear] = useState(year || yearOptions[0].value);

  const [isLoading, setIsLoading] = useState(false);

  const [risingStarCollection, setRisingStarCollection] = useState({
    [dropdownYear]: awardGroups,
  });

  const dispatchRisingStarCTAClickEvent = (selectedYear) => {
    const clickTarget = RISING_STAR_CLICK_TARGET(selectedYear);

    dispatch(
      ctaClickEvent(RISING_STAR_CLICK_AREA, clickTarget, {
        clickStyle: DROPDOWN_CTA_STYLE,
        name: RISING_STAR_PAGE_NAME,
      })
    );
  };

  const handleChange = async (e) => {
    setIsLoading(true);
    const updatedYear = e.target.value;

    setDropdownYear(updatedYear);
    dispatchRisingStarCTAClickEvent(updatedYear);

    let updatedYearRisingStarContent = {};

    if (isEmpty(risingStarCollection[updatedYear])) {
      const { awardGroups: updatedYearAwardGroups } = await RisingStarApi.getRisingStarPageContent(
        updatedYear
      );

      updatedYearRisingStarContent = {
        [updatedYear]: updatedYearAwardGroups,
      };
    }

    setRisingStarCollection({
      ...risingStarCollection,
      ...updatedYearRisingStarContent,
    });

    const url = new URL(window.location);
    url.pathname = `/rising-star/${updatedYear}`;
    window.history.pushState(null, '', url.toString());

    setIsLoading(false);
  };

  return (
    <>
      <MetaDataMarkup
        title={metaTitle}
        description={metaDescription}
        openGraphProps={{
          title: metaTitle,
          description: metaDescription,
          type: 'website',
          url: `https://${gmConfig.domains.www}/rising-star${year ? `/${year}` : ''}`,
        }}
        preloadImages={[{ src: CONFETTI_HERO_IMAGE_LEFT }, { src: CONFETTI_HERO_IMAGE_RIGHT }]}
      />
      <HeroWithConfetti
        title={title}
        subtitle={RISING_STAR_PAGE_DESCRIPTION}
        backgroundColor="#FFFFFF"
        large
      />
      <StyledSelect
        id="rising-star-year"
        testid="rising-star-year"
        name="rising-star-year"
        value={dropdownYear}
        onChange={handleChange}
        options={yearOptions}
        native
        disablePlaceholder
      />
      {isLoading ? (
        <Loader />
      ) : (
        <SectionContainer maxWidth="lg">
          {risingStarCollection[dropdownYear].map((g) => (
            <AwardGroupWinnerList
              key={g.name}
              awardGroupName={g.name}
              winners={g.winners}
              year={dropdownYear}
            />
          ))}
        </SectionContainer>
      )}
    </>
  );
};

RisingStarPageView.propTypes = {
  title: PropTypes.string.isRequired,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  awardGroups: PropTypes.arrayOf(awardGroupPropType).isRequired,
  publishedYears: PropTypes.arrayOf(PropTypes.number).isRequired,
};

RisingStarPageView.defaultProps = {
  year: null,
};

export default RisingStarPageView;
